/**
 * Super simple "stage" abstraction for canvas. Combined with Ticker.js, it helps simplify:
 *   - Preparing a canvas for drawing.
 *   - High resolution rendering.
 *   - Resizing the canvas.
 *   - Pointer events (mouse and touch).
 *   - Frame callbacks with useful timing data and calculated lag.
 *
 * This is no replacement for robust canvas drawing libraries; it's designed to be as lightweight as possible and defers
 * full rendering burden to user.
 *
 * Author: Caleb Miller
 *         caleb@caleb-miller.com
*/

import React from 'react';
import PropTypes from 'prop-types';

import Ticker from 'helpers/Ticker';




class Canvas extends React.PureComponent {

	constructor(props) {
		super(props);

		// devicePixelRatio alias (should only be used for rendering, physics shouldn't care).
		// Avoids rendering unnecessary pixels that browser might handle natively via CanvasRenderingContext2D.backingStorePixelRatio.
		// Set in `storeCanvas()` method.
		this.dpr = 1;

		// Track touch times to prevent redundant mouse events.
		this.lastTouchTimestamp = 0;
		// Track last known pointer position to "fill in" position data for touchend events.
		this.lastPointerPos = { x: 0, y: 0 };

		this.storeCanvas = this.storeCanvas.bind(this);
		this.handleTick = this.handleTick.bind(this);
		this.handleMouseEvent = this.handleMouseEvent.bind(this);
		this.handleTouchEvent = this.handleTouchEvent.bind(this);
		this.dispatchPointerDown = this.dispatchPointerDown.bind(this);
		this.dispatchPointerMove = this.dispatchPointerMove.bind(this);
		this.dispatchPointerUp = this.dispatchPointerUp.bind(this);
	}

	componentDidMount() {
		if (this.props.onTick) {
			this.unsubscribeTicker = Ticker.subscribe(this.handleTick);
		}

		// Detect interactivity and add event listeners
		if (this.props.onPointerDown || this.props.onPointerMove || this.props.onPointerUp) {
			// Prevent gestures on interactive canvas (scrolling, zooming, etc)
			this.canvasNode.style.touchAction = 'none';
			document.addEventListener('mousedown', this.handleMouseEvent);
			document.addEventListener('mousemove', this.handleMouseEvent);
			document.addEventListener('mouseup', this.handleMouseEvent);
			document.addEventListener('touchstart', this.handleTouchEvent);
			document.addEventListener('touchmove', this.handleTouchEvent);
			document.addEventListener('touchend', this.handleTouchEvent);
		}
	}

	componentWillUnmount() {
		this.unsubscribeTicker && this.unsubscribeTicker();

		if (this.props.onPointerDown || this.props.onPointerMove || this.props.onPointerUp) {
			document.removeEventListener('mousedown', this.handleMouseEvent);
			document.removeEventListener('mousemove', this.handleMouseEvent);
			document.removeEventListener('mouseup', this.handleMouseEvent);
			document.removeEventListener('touchstart', this.handleTouchEvent);
			document.removeEventListener('touchmove', this.handleTouchEvent);
			document.removeEventListener('touchend', this.handleTouchEvent);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { width, height } = this.props;
		if (this.canvasNode) {
			if (width !== prevProps.width) {
				this.canvasNode.width = width * this.dpr;
			}
			if (height !== prevProps.height) {
				this.canvasNode.height = height * this.dpr;
			}
		}
	}

	// Store and setup canvas.
	storeCanvas(canvasNode) {
		this.canvasNode = canvasNode;
		this.ctx = canvasNode && canvasNode.getContext('2d');

		if (canvasNode) {
			this.dpr = (window.devicePixelRatio || 1) / (this.ctx.backingStorePixelRatio || 1);
			canvasNode.width = this.props.width * this.dpr;
			canvasNode.height = this.props.height * this.dpr;
		}
	}

	// Call user-provided `onTick`, passing useful Ticker/canvas data along.
	// Additionally takes care of scaling graphics for high DPI drawing automatically.
	handleTick(frameTime, lag) {
		const { dpr, ctx } = this;
		const { onTick, width, height, autoScale } = this.props;

		autoScale && ctx.scale(dpr, dpr);

		const dprScale = autoScale ? 1 : dpr;

		onTick(
			frameTime,
			lag,
			ctx,
			width * dprScale,
			height * dprScale
		);

		autoScale && ctx.setTransform(1, 0, 0, 1, 0, 0);
	}

	handleMouseEvent(evt) {
		// Prevent mouse events from firing immediately after touch events
		if (Date.now() - this.lastTouchTimestamp < 500) {
			return;
		}

		const pos = this.windowToCanvas(evt.clientX, evt.clientY);

		if (evt.type === 'mousedown') {
			this.dispatchPointerDown(pos);
		} else if (evt.type === 'mousemove') {
			this.dispatchPointerMove(pos);
		} else if (evt.type === 'mouseup') {
			this.dispatchPointerUp(pos);
		}
	}

	handleTouchEvent(evt) {
		this.lastTouchTimestamp = Date.now();

		let dispatchMethod;
		if (evt.type === 'touchstart') {
			dispatchMethod = this.dispatchPointerDown;
		} else if (evt.type === 'touchmove') {
			dispatchMethod = this.dispatchPointerMove;
		} else if (evt.type === 'touchend') {
			dispatchMethod = this.dispatchPointerUp;
		}

		// Safari doesn't treat a TouchList as an iteratable, hence Array.from()
		for (let touch of Array.from(evt.changedTouches)) {
			let pos;
			if (evt.type !== 'touchend') {
				pos = this.windowToCanvas(touch.clientX, touch.clientY);
				this.lastPointerPos = pos;
				// before touchstart event, fire a move event to better emulate cursor events
				if (evt.type === 'touchstart') {
					this.dispatchPointerMove(pos);
				}
			} else {
				// on touchend, fill in position information based on last known touch location
				pos = this.lastPointerPos;
			}

			dispatchMethod(pos);
		}
	}

	windowToCanvas(x, y) {
		const canvas = this.canvasNode;
		const bbox = canvas.getBoundingClientRect();
		const pos = {
			x: (x - bbox.left) * (canvas.width / bbox.width),
			y: (y - bbox.top) * (canvas.height / bbox.height)
		};

		if (this.props.autoScale) {
			pos.x /= this.dpr;
			pos.y /= this.dpr;
		}

		return pos;
	}


	// Dispatch custom "pointer" events. These aren't spec complient, and have just enough info for common uses.
	// Must provide a position object with `x` and `y` properties.
	dispatchPointerDown(position) {
		this.props.onPointerDown && this.props.onPointerDown(position);
	}

	dispatchPointerMove(position) {
		this.props.onPointerMove && this.props.onPointerMove(position);
	}

	dispatchPointerUp(position) {
		this.props.onPointerUp && this.props.onPointerUp(position);
	}

	render() {
		const { className, style } = this.props;

		return (
			<canvas
				ref={this.storeCanvas}
				className={className}
				style={style}
			/>
		);
	}
}

export default Canvas;


Canvas.defaultProps = {
	autoScale: true
};

Canvas.propTypes = {
	// Width/height always in DIPs
	width: PropTypes.number.isRequired,
	height: PropTypes.number.isRequired,
	// Whether to auto-scale draw calls on high-dpi screens
	autoScale: PropTypes.bool.isRequired,
	// Callbacks
	onTick: PropTypes.func,
	onPointerDown: PropTypes.func,
	onPointerMove: PropTypes.func,
	onPointerUp: PropTypes.func
};



// WEBPACK FOOTER //
// ./src/ui/Canvas.jsx